import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getCurrentPage,
  getInspectionId,
  getTextMatchCdnPathPrefix,
  getTextMatchJobId,
  getTextMatchMetadata,
} from 'store';
import { useTextMatchPages } from 'store/queries/inspections/useTextMatches';

/** How many pages either side of the current one to warm the cache for. */
const BUFFER_PAGES = 2;

/**
 * Warms the CDN cache for text matches on the pages around the ones currently in view, so that
 * navigating between text matches doesn't wait on a fetch. The active query observers also pin
 * those cache entries, which is what lets the Ctrl/Cmd+click handler in `EventHandlers.ts` read
 * them synchronously via `getQueryData` — on a miss that click silently does nothing.
 *
 * Only call this from a component that renders (close to) nothing. It subscribes to the current
 * page, which changes on every scroll tick, so the caller re-renders constantly. `ResultTab` is
 * the intended home; calling it from a component with real markup will make scrolling janky.
 */
export const useTextMatchPagePrefetch = () => {
  const inspectionId = useSelector(getInspectionId);
  const textMatchJobId = useSelector(getTextMatchJobId);
  const textMatchCdnPathPrefix = useSelector(getTextMatchCdnPathPrefix);
  const textMatchMetadata = useSelector(getTextMatchMetadata);
  const currentPages = useSelector(getCurrentPage);

  const pagesToFetch = useMemo(() => {
    if (!textMatchMetadata) return [];
    const pages: { side: 'master' | 'sample'; page: number }[] = [];

    for (let offset = -BUFFER_PAGES; offset <= BUFFER_PAGES; offset++) {
      const masterPage = currentPages.source + offset;
      if (masterPage >= 1 && textMatchMetadata.masterPages[String(masterPage)]) {
        pages.push({ side: 'master', page: masterPage });
      }
      const samplePage = currentPages.target + offset;
      if (samplePage >= 1 && textMatchMetadata.samplePages[String(samplePage)]) {
        pages.push({ side: 'sample', page: samplePage });
      }
    }
    return pages;
  }, [textMatchMetadata, currentPages.source, currentPages.target]);

  // Active observers prevent GC of the fetched pages and auto-clean on unmount.
  useTextMatchPages(inspectionId, textMatchJobId, pagesToFetch, textMatchCdnPathPrefix);
};
